/**
 * MÓDULOS, PERMISOS Y MENÚ
 * ========================
 *
 * Dos ejes distintos filtran lo que una persona ve, y se modelan separados (política 5 de
 * RASP):
 *
 *   PLAN      ¿la entidad COMPRÓ este módulo?   → comercial      (Plan + ModuloEntidad)
 *   PERMISOS  ¿esta persona puede usarlo?       → organizacional (Rol.permisos)
 *
 * El menú efectivo es la intersección, y se CALCULA de los permisos: no hay un archivo de
 * menú aparte que pueda desincronizarse y llevar a un 403. Separados, además, permiten decir
 * "esto no está en tu plan" —que es una venta— en vez de "no tenés permiso", que es un
 * callejón.
 *
 * El vocabulario de permisos es código porque cada clave protege endpoints que son código.
 * Lo que es DATO es qué permisos tiene cada rol: eso lo arma cada entidad, y por eso agregar
 * un rol no es un deploy.
 */

export type ClaveModulo =
  | 'TABLERO'
  | 'MOSTRADOR'
  | 'INTEGRANTES'
  | 'ACTIVIDADES'
  | 'LISTADO'
  | 'PAGOS'
  | 'PADRON'
  | 'CATALOGOS'
  | 'REPORTES'
  | 'AUDITORIA'
  | 'EQUIPO';

export interface DefinicionModulo {
  clave: ClaveModulo;
  nombre: string;
  descripcion: string;
  ruta: string;
  icono: string;
  /**
   * Los módulos base vienen con cualquier plan. Son el producto: sin mostrador, integrantes,
   * actividades y pagos no hay Planilla, y sin catálogos ni equipo no se puede configurar.
   * Lo que se vende aparte es la mirada (reportes) y la prueba (auditoría). El padrón pasó a ser
   * base (2026-09-26): traer los datos al tipear la cédula era parte del pedido original, no un
   * agregado, y sin él cargar gente en una actividad es tipear cada nombre a mano.
   */
  base: boolean;
  /**
   * `false` = no tiene pantalla propia y NO va al menú. El padrón es ayuda para tipear: vive dentro
   * del alta de una persona (en la actividad, en Integrantes, en el mostrador), no en una sección
   * aparte. Reportes todavía no tiene pantalla: un ítem de menú que no lleva a nada es peor que
   * no tenerlo.
   */
  enMenu?: false;
  /**
   * Permisos de OTROS módulos que también hacen aparecer este en el menú. Listado no tiene
   * permisos propios (solo mira lo que ya dejan ver Actividades y el Mostrador), y Catálogos
   * aparece también para quien ve personas, que ahora viven adentro de Catálogos.
   */
  verCon?: readonly string[];
}

export const MODULOS: readonly DefinicionModulo[] = [
  { clave: 'TABLERO', nombre: 'Tablero', descripcion: 'Cómo va la jornada: pagados, pendientes y entregado de hoy', ruta: '/', icono: 'tablero', base: true },
  { clave: 'MOSTRADOR', nombre: 'Mostrador', descripcion: 'Tipear una cédula y saber si hay que pagarle o ya cobró', ruta: '/mostrador', icono: 'mostrador', base: true },
  // Integrantes no va al menú: vive dentro de Catálogos como "Personas" (Rodrigo, 2026-09-26).
  { clave: 'INTEGRANTES', nombre: 'Integrantes', descripcion: 'Las personas de la organización y sus datos', ruta: '/integrantes', icono: 'integrantes', base: true, enMenu: false },
  { clave: 'ACTIVIDADES', nombre: 'Actividades', descripcion: 'Cada trabajo hecho y quiénes participaron', ruta: '/actividades', icono: 'actividades', base: true },
  { clave: 'LISTADO', nombre: 'Listado', descripcion: 'Todos los registrados y pagados de una actividad, a pantalla completa', ruta: '/listado', icono: 'integrantes', base: true, verCon: ['actividades.ver', 'mostrador.consultar'] },
  { clave: 'PAGOS', nombre: 'Pagos', descripcion: 'Lo entregado, con su comprobante y sus anulaciones', ruta: '/pagos', icono: 'pagos', base: true },
  { clave: 'PADRON', nombre: 'Padrón', descripcion: 'Ayuda para tipear: propone el nombre a partir de la cédula', ruta: '/padron', icono: 'padron', base: true, enMenu: false },
  { clave: 'CATALOGOS', nombre: 'Catálogos', descripcion: 'Personas, zonas, tipos de actividad, medios de pago y datos de la entidad', ruta: '/catalogos', icono: 'catalogos', base: true, verCon: ['integrantes.ver'] },
  { clave: 'REPORTES', nombre: 'Reportes', descripcion: 'Qué se pagó por zona, por actividad y por período', ruta: '/reportes', icono: 'reportes', base: false, enMenu: false },
  { clave: 'AUDITORIA', nombre: 'Auditoría', descripcion: 'Quién hizo qué, cuándo y desde dónde', ruta: '/auditoria', icono: 'auditoria', base: false },
  { clave: 'EQUIPO', nombre: 'Equipo', descripcion: 'Quién entra al sistema y con qué rol', ruta: '/equipo', icono: 'equipo', base: true },
];

/** Los módulos que se venden. Los base no figuran en un plan porque vienen siempre. */
export const MODULOS_VENDIBLES: readonly ClaveModulo[] = MODULOS.filter((m) => !m.base).map((m) => m.clave);

export interface DefinicionPermiso {
  clave: string;
  modulo: ClaveModulo;
  nombre: string;
}

/**
 * Granularidad `modulo.accion`. Solo por módulo sería demasiado grueso: un cajero tiene que
 * poder pagar y no poder anular, que es la diferencia entre un error y un desfalco.
 */
export const PERMISOS = [
  { clave: 'tablero.ver', modulo: 'TABLERO', nombre: 'Ver el tablero de la jornada' },

  { clave: 'mostrador.consultar', modulo: 'MOSTRADOR', nombre: 'Consultar una cédula en el mostrador' },
  { clave: 'mostrador.pagar', modulo: 'MOSTRADOR', nombre: 'Entregar el pago y emitir el comprobante' },
  { clave: 'mostrador.inscribir', modulo: 'MOSTRADOR', nombre: 'Dar de alta en el mostrador a quien no estaba en la lista' },

  { clave: 'integrantes.ver', modulo: 'INTEGRANTES', nombre: 'Ver integrantes' },
  { clave: 'integrantes.crear', modulo: 'INTEGRANTES', nombre: 'Dar de alta integrantes' },
  { clave: 'integrantes.editar', modulo: 'INTEGRANTES', nombre: 'Editar integrantes' },
  { clave: 'integrantes.baja', modulo: 'INTEGRANTES', nombre: 'Dar de baja integrantes' },
  { clave: 'integrantes.importar', modulo: 'INTEGRANTES', nombre: 'Cargar integrantes en lote' },

  { clave: 'actividades.ver', modulo: 'ACTIVIDADES', nombre: 'Ver actividades y su lista' },
  { clave: 'actividades.crear', modulo: 'ACTIVIDADES', nombre: 'Crear actividades' },
  { clave: 'actividades.editar', modulo: 'ACTIVIDADES', nombre: 'Editar actividades y armar la lista' },
  { clave: 'actividades.abrir', modulo: 'ACTIVIDADES', nombre: 'Abrir y cerrar una actividad para el mostrador' },
  { clave: 'actividades.anular', modulo: 'ACTIVIDADES', nombre: 'Anular una actividad' },

  { clave: 'pagos.ver', modulo: 'PAGOS', nombre: 'Ver los pagos y sus comprobantes' },
  { clave: 'pagos.anular', modulo: 'PAGOS', nombre: 'Anular un pago' },
  { clave: 'pagos.autorizar', modulo: 'PAGOS', nombre: 'Dar el visto bueno a un monto distinto al de la actividad' },

  { clave: 'padron.consultar', modulo: 'PADRON', nombre: 'Consultar el padrón de ayuda por cédula' },

  { clave: 'zonas.gestionar', modulo: 'CATALOGOS', nombre: 'Gestionar zonas' },
  { clave: 'tipos.gestionar', modulo: 'CATALOGOS', nombre: 'Gestionar tipos de actividad' },
  { clave: 'medios.gestionar', modulo: 'CATALOGOS', nombre: 'Gestionar medios de pago' },
  { clave: 'entidad.configurar', modulo: 'CATALOGOS', nombre: 'Editar los datos y las reglas de la entidad' },

  { clave: 'reportes.ver', modulo: 'REPORTES', nombre: 'Ver los reportes' },

  { clave: 'auditoria.ver', modulo: 'AUDITORIA', nombre: 'Consultar la auditoría' },

  { clave: 'usuarios.gestionar', modulo: 'EQUIPO', nombre: 'Gestionar quién entra al sistema' },
  { clave: 'roles.gestionar', modulo: 'EQUIPO', nombre: 'Gestionar roles y permisos' },
] as const satisfies readonly DefinicionPermiso[];

export type ClavePermiso = (typeof PERMISOS)[number]['clave'];

/** '*' concede todos los permisos, incluidos los que se agreguen después. */
export const TODOS_LOS_PERMISOS = '*';

export function esPermisoValido(clave: string): boolean {
  return clave === TODOS_LOS_PERMISOS || PERMISOS.some((p) => p.clave === clave);
}

export function tienePermiso(permisos: readonly string[], requerido: ClavePermiso): boolean {
  return permisos.includes(TODOS_LOS_PERMISOS) || permisos.includes(requerido);
}

export function moduloDePermiso(clave: string): ClaveModulo | null {
  return PERMISOS.find((p) => p.clave === clave)?.modulo ?? null;
}

/**
 * Módulos habilitados para una entidad: los base siempre, los vendibles según el plan, y las
 * banderas por entidad mandan sobre el plan en los dos sentidos (habilitar algo que el plan
 * no trae, o apagar algo que sí trae, dejando anotado por qué).
 */
export function modulosHabilitados(
  modulosDelPlan: readonly string[],
  banderas: ReadonlyArray<{ clave: string; habilitado: boolean }>,
): ClaveModulo[] {
  const delPlan = new Set(modulosDelPlan);
  const porClave = new Map(banderas.map((b) => [b.clave, b.habilitado]));
  return MODULOS.filter((modulo) => {
    if (modulo.base) return true;
    return porClave.get(modulo.clave) ?? delPlan.has(modulo.clave);
  }).map((m) => m.clave);
}

export interface EntradaMenu {
  clave: ClaveModulo;
  nombre: string;
  ruta: string;
  icono: string;
  /** Visible pero no contratado: se muestra con candado y lleva a "sumalo a tu plan". */
  bloqueado: boolean;
}

/**
 * El menú es una PROYECCIÓN de permisos ∩ módulos. Un módulo aparece si la persona tiene al
 * menos un permiso de ese módulo, si la entidad lo tiene contratado y si tiene pantalla propia.
 *
 * Lo no contratado NO se muestra, ni siquiera con candado y ni siquiera a quien administra
 * (Rodrigo, 2026-09-26: "no debería aparecer menús que no asigné"). Quien usa esto no está
 * mirando qué más podría comprar: está tratando de cargar gente y pagar. La venta la hace RASP.
 * `bloqueado` queda en el contrato por compatibilidad y hoy es siempre false.
 */
export function calcularMenu(
  permisos: readonly string[],
  habilitados: readonly ClaveModulo[],
): EntradaMenu[] {
  const todo = permisos.includes(TODOS_LOS_PERMISOS);
  const deModulo = new Set(permisos.map(moduloDePermiso).filter(Boolean));
  return MODULOS.filter(
    (modulo) =>
      modulo.enMenu !== false &&
      habilitados.includes(modulo.clave) &&
      (todo || deModulo.has(modulo.clave) || (modulo.verCon ?? []).some((permiso) => permisos.includes(permiso))),
  ).map((modulo) => ({
    clave: modulo.clave,
    nombre: modulo.nombre,
    ruta: modulo.ruta,
    icono: modulo.icono,
    bloqueado: false,
  }));
}

/** Permisos efectivos: los del rol, recortados a los módulos habilitados. */
export function permisosEfectivos(
  permisosDelRol: readonly string[],
  habilitados: readonly ClaveModulo[],
): string[] {
  const todos = permisosDelRol.includes(TODOS_LOS_PERMISOS)
    ? PERMISOS.map((p) => p.clave as string)
    : permisosDelRol.filter(esPermisoValido);
  return todos.filter((clave) => {
    const modulo = moduloDePermiso(clave);
    return modulo !== null && habilitados.includes(modulo);
  });
}

/**
 * ROLES CON LOS QUE NACE UNA ENTIDAD
 *
 * Son un punto de partida clonable y editable, no una jaula: cada organización arma su
 * equipo distinto y tiene que poder crear el suyo sin pedirle nada a RASP.
 *
 * Los cuatro salen de cómo trabaja una sucursal el día de pago:
 *   Administrador  el que configura y responde por todo.
 *   Cajero         el que está en el mostrador. Paga y NO anula: anular es de otro.
 *   Dirigente      carga y mantiene integrantes, ve las actividades, NO paga.
 *   Auditor        mira todo y no toca nada.
 *
 * CADA DESCRIPCIÓN DICE EXACTAMENTE LO QUE LOS PERMISOS HACEN, y eso es una regla y no un
 * detalle de redacción: el texto es lo único que lee quien asigna el rol. Un rol que se
 * describe más acotado de lo que es no se queda corto —engaña—, y alguien le da ese rol a una
 * persona creyendo que la limita. Es peor que no ofrecer el rol.
 */
export const ROLES_PLANTILLA: ReadonlyArray<{ nombre: string; descripcion: string; permisos: string[] }> = [
  {
    nombre: 'Administrador',
    descripcion: 'Acceso total a la entidad, incluidos equipo, catálogos y auditoría.',
    permisos: [TODOS_LOS_PERMISOS],
  },
  {
    nombre: 'Cajero',
    descripcion: 'Atiende el mostrador: consulta la cédula, paga y emite el comprobante. No anula pagos.',
    permisos: [
      'mostrador.consultar',
      'mostrador.pagar',
      'mostrador.inscribir',
      'pagos.ver',
      'integrantes.ver',
      // Sin esto, el alta en el mostrador obliga a tipear el nombre completo a mano.
      'padron.consultar',
    ],
  },
  {
    // OJO: este rol NO está acotado por zona, y la descripción no lo promete.
    //
    // Acotar a un dirigente a su seccional necesitaría una columna en `Membresia` (o una tabla
    // puente usuario ↔ zona) y un filtro en cada consulta de integrantes y de participaciones. No
    // existe ninguna de las dos cosas, así que un dirigente ve TODOS los integrantes de la entidad.
    //
    // Queda pendiente de una decisión de producto que Rodrigo todavía no tomó: si el alcance se
    // acota por zona, por referente, o si no se acota. Hasta que esté tomada, la descripción dice
    // lo que los permisos hacen de verdad —si dijera "su zona", quien le asigna el rol creería que
    // lo limita y no lo limita—.
    nombre: 'Dirigente',
    // El alcance se dice en positivo y no como una negación ("no solo su zona"): en un selector de
    // roles se lee media frase, y media negación se entiende como lo contrario de lo que dice.
    descripcion: 'Carga y mantiene integrantes, y ve las actividades. No paga ni anula. Alcance: toda la entidad.',
    permisos: [
      'tablero.ver',
      'integrantes.ver',
      'integrantes.crear',
      'integrantes.editar',
      'integrantes.importar',
      'actividades.ver',
      'padron.consultar',
    ],
  },
  {
    nombre: 'Auditor',
    descripcion: 'Solo lectura de todo lo operativo, más la auditoría.',
    permisos: ['tablero.ver', 'integrantes.ver', 'actividades.ver', 'pagos.ver', 'reportes.ver', 'auditoria.ver'],
  },
];

/** Medios de pago con los que nace una entidad. Los edita desde sus catálogos. */
export const MEDIOS_PAGO_INICIALES: ReadonlyArray<{ nombre: string; exigeReferencia: boolean; orden: number }> = [
  { nombre: 'Efectivo', exigeReferencia: false, orden: 1 },
  { nombre: 'Transferencia', exigeReferencia: true, orden: 2 },
];

/** Tipos de actividad de ejemplo. Cada entidad los renombra y agrega los suyos. */
export const TIPOS_ACTIVIDAD_INICIALES: ReadonlyArray<{ nombre: string; descripcion: string; orden: number }> = [
  { nombre: 'Caravana', descripcion: 'Recorrida con vehículos y banderas.', orden: 1 },
  { nombre: 'Volanteada', descripcion: 'Reparto de material en la calle.', orden: 2 },
  { nombre: 'Reunión', descripcion: 'Encuentro en un local o una casa.', orden: 3 },
  { nombre: 'Jornada', descripcion: 'Día completo de trabajo en territorio.', orden: 4 },
];
